import React, { SyntheticEvent, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { debounce } from "lodash";
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Box,
  Button,
  FormHelperText,
  InputAdornment,
  TextField,
  Typography,
  Paper,
  Grid,
  AutocompleteRenderInputParams,
  SxProps,
  Theme,
} from "@mui/material";

import VirtualizeAutocomplete from "../../../formComponents/AutocompleteList";
import {
  NppesAutocompleteUI,
  IconFaUser,
  NppesAutoComplete,
  IconFaSearch,
  Search,
  NppesAutoCompleteNameInputMargin,
  NppesContainer,
  rowMaxWidth,
  NppesErrorMsgStyle,
  NppesOuterBodyGrid,
  NppesStateSearchWidth,
  SearchNpiContainer,
  DatePickerInput,
} from "../styles/style";
import {
  getTableBody,
  getTableHead,
  TableDropdown,
} from "../../../../constants/AllPatientRecord";
import fontWeight from "../../../../styles/mui/fontWeight";
import { colors } from "../../../../styles/colors";
import { NO_RECORD_FOUND } from "../../../../constants/ToastMessage";
import useMenuItem from "../../../../hooks/useMenuItem";
import { stateNameAbbreviations } from "../../../../constants/StateAbbreviations";
import { length, getValue, isNil, stateListData } from "../../../../utils";
import {
  GetNPPESDataActionDispatchTypes,
  NppesApiCall,
  NPPESinitialRecord,
  NppesNameErrorMsg,
  physicianInitialData,
} from "../../../../constants/PhysicianDetails";
import {
  NppesAutoCompletePaper,
  AvailableRecords,
  StyledPopper,
  StyledPopperCheckBoxAutocomplete,
} from "../../../formComponents/styles/style";
import {
  NppesFadedContainedButtonUI,
  NppesNPIPadding,
  NppesNPISearchPadding,
} from "../../../../styles/common/style";
import { NPPESDataResponse } from "../../../../models/Api/PhysicianDetails";
import { NppesPhysician } from "../../../../models/PhysicianDetails";
import {
  flexAlignCentre,
  flexAlignEnd,
  flexAllCentre,
} from "../../../../styles/mui/styles/display";

export interface PropsFromState {
  NppesOpen: boolean;
  NppesSetOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleSelect: React.Dispatch<React.SetStateAction<any>>;
  PhysicianDetailsData: NPPESDataResponse;
  openAutocomplete: boolean;
  setOpenAutocomplete: React.Dispatch<React.SetStateAction<boolean>>;
  nppesSearchInjectedto?: string;
}

export interface PropsFromDispatch {
  resetStates?: (actionType: string[]) => void;
  getNPPESDetails: (payload: any) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const NppesSearchComponent: React.FC<AllProps> = ({
  NppesOpen,
  NppesSetOpen,
  handleSelect,
  PhysicianDetailsData,
  getNPPESDetails,
  resetStates,
  openAutocomplete,
  setOpenAutocomplete,
  nppesSearchInjectedto,
}: AllProps) => {
  const { open, onOpen, onClose } = useMenuItem();
  const [stateList, setStateList] = useState<any[]>([]);
  const [stateName, setStateName] = useState<any>();
  const [payloadStateName, setPayloadStateName] = useState<any>(null);
  const [lastNameinputValue, setLastNameInputValue] = useState<string>("");
  const [firstNameInputValue, setFirstNameInputValue] = useState<string>("");
  const [PhysicianListByNameResp, setPhysicianListByNameResp] = useState<
    NppesPhysician[]
  >([]);
  const [PhysicianListByNPIResp, setPhysicianListByNPIResp] = useState<
    NppesPhysician[]
  >([]);
  const [apicallIdentifier, setApiCallIdentifier] = useState<string>("");
  const { response, loading } = PhysicianDetailsData;
  const [npiNumberLength, setNpiNumberLength] = useState<string>("");

  const autocompleteRef = useRef<HTMLInputElement | null>(null);
  const BtnRef = useRef<HTMLButtonElement | null>(null);
  const lastNameFieldRef = useRef<HTMLInputElement | null>(null);
  const firstNameFieldRef = useRef<HTMLInputElement | null>(null);

  const methods = useForm<any>({
    defaultValues: null,
    mode: "all",
  });

  const { control, handleSubmit } = methods;

  const debouncedSearch = debounce((event, value, reason) => {
    handleNppesName(event, value, reason);
  }, 500);

  useEffect(() => {
    if (getValue(response, "[0].physicianName", "") !== "") {
      if (
        apicallIdentifier === NppesApiCall.BY_NAME &&
        PhysicianListByNameResp.length === 0
      ) {
        setPhysicianListByNPIResp([]);
        setPhysicianListByNameResp([...NPPESinitialRecord, ...response]);
      } else if (apicallIdentifier === NppesApiCall.BY_NPI) {
        setPhysicianListByNameResp([]);
        setPhysicianListByNPIResp([...response]);
      }
    } else {
      setPhysicianListByNameResp(() => []);
      setPhysicianListByNPIResp(() => []);
    }
  }, [response]);

  const removeEventListener = () => {
    window.removeEventListener("scroll", closeDropdown);
    document.removeEventListener("mousedown", closeMuiDropdown);
  };

  useEffect(() => {
    if (openAutocomplete) {
      window.addEventListener("scroll", closeDropdown);
      document.addEventListener("mousedown", closeMuiDropdown);
    } else {
      removeEventListener();
    }
    return removeEventListener;
  }, [openAutocomplete]);

  useEffect(() => {
    if (npiNumberLength.length < 10) {
      NppesSetOpen && NppesSetOpen(false);
      setPhysicianListByNPIResp(() => []);
      setOpenAutocomplete(false);
    }
  }, [npiNumberLength]);

  const handleFocus = () => {
    if (npiNumberLength.length === 10) {
      setApiCallIdentifier(NppesApiCall.BY_NPI);
      NppesSetOpen && NppesSetOpen(true);
      const payload = {
        npi: npiNumberLength,
      };
      getNPPESDetails(payload);
    } else {
      NppesSetOpen && NppesSetOpen(false);
    }
  };

  const refs = [autocompleteRef, BtnRef, firstNameFieldRef, lastNameFieldRef];

  const closeMuiDropdown = (event: MouseEvent) => {
    setTimeout(() => {
      const isOutsideClick = refs.every(
        (ref) => ref.current && !ref.current.contains(event.target as Node)
      );

      if (isOutsideClick) {
        setOpenAutocomplete(false);
        setPhysicianListByNameResp([]);
      }
    }, 0);
  };

  const closeDropdown = () => {
    setOpenAutocomplete(false);
    setPhysicianListByNameResp(() => []);
    setPhysicianListByNPIResp(() => []);
  };

  const handleInputChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    setPhysicianListByNameResp(() => []);
    debouncedSearch(event, value, reason);
    setNpiNumberLength(value.trim());
  };

  const handleNppesName = (
    event: React.SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    setApiCallIdentifier("");
    setPhysicianListByNameResp([]);
    if (!isNil(value) && !isNil(event)) {
      if (value.length < 10) {
        NppesSetOpen && NppesSetOpen(false);
      }
      if (value.trim().length === 10 && reason === "input") {
        setApiCallIdentifier(NppesApiCall.BY_NPI);
        setPhysicianListByNameResp([]);
        resetStates &&
          resetStates([GetNPPESDataActionDispatchTypes.GET_NPPES_DATA_RESET]);
        NppesSetOpen && NppesSetOpen(true);
        const payload = {
          npi: value,
        };
        getNPPESDetails(payload);
      }
    }
  };

  const validateNPPESNames = () => {
    const firstNameFilled =
      firstNameInputValue && firstNameInputValue.trim() !== "";
    const lastNameFilled =
      lastNameinputValue && lastNameinputValue.trim() !== "";

    return firstNameFilled || lastNameFilled || NppesNameErrorMsg;
  };

  useEffect(() => {
    const statesArray = stateListData();
    setStateList(statesArray);
  }, [stateNameAbbreviations]);

  const defaultProps = (stateList: any[] | null | undefined) => ({
    options: stateList as any[],
    getOptionLabel: (option: any) => option.stateName,
  });

  const onSelection = (
    _e: SyntheticEvent<Element, Event>,
    value: any | null
  ) => {
    setStateName(value);
    const stateNameData =
      value != null && !isNil(stateList)
        ? stateList &&
          stateList.find(
            (v: any) =>
              v.stateName === value.stateName && v.stateId === value.stateId
          )
        : null;
    setPayloadStateName(stateNameData === null ? null : stateNameData.stateId);
    onClose();
  };

  const handleChoose = (option?: NppesPhysician) => {
    handleSelect(option);
    setOpenAutocomplete(false);
    setPhysicianListByNameResp(() => []);
    setPhysicianListByNPIResp(() => []);
  };

  const onSubmit = () => {
    setOpenAutocomplete(true);
    setApiCallIdentifier(NppesApiCall.BY_NAME);
    resetStates &&
      resetStates([GetNPPESDataActionDispatchTypes.GET_NPPES_DATA_RESET]);
    const payload = {
      firstName: firstNameInputValue && firstNameInputValue + "*",
      lastName: lastNameinputValue && lastNameinputValue + "*",
      state: payloadStateName,
    };
    getNPPESDetails(payload);
  };

  const handleSearch = () => {
    setPhysicianListByNPIResp([]);
    handleSubmit(onSubmit)();
  };

  const header = (position: any, headerTitle: string, sx: SxProps<Theme>) => {
    return (
      <Box textAlign={position} sx={sx}>
        <Typography
          variant="body1"
          color={colors.black[14]}
          fontWeight={fontWeight.Weight[5]}
        >
          {headerTitle}
        </Typography>
      </Box>
    );
  };

  const body = (
    value: any,
    isButton: boolean,
    sx: SxProps<Theme>,
    option?: NppesPhysician
  ) => {
    return (
      <Box sx={sx}>
        {isButton === false ? (
          <Typography
            variant="subtitle2"
            color={colors.black[4]}
            fontWeight={fontWeight.Weight[4]}
          >
            {value}
          </Typography>
        ) : (
          <Button
            variant="contained"
            sx={[NppesFadedContainedButtonUI]}
            onClick={() => handleChoose(option)}
            ref={BtnRef}
            data-testid={`${nppesSearchInjectedto}-nppes-choose`}
          >
            CHOOSE
          </Button>
        )}
      </Box>
    );
  };
  const customPaper = (props: any) => {
    return (
      <Paper
        {...props}
        sx={NppesAutoCompletePaper("55rem")}
        ref={autocompleteRef}
      />
    );
  };

  return (
    <Grid container>
      <Grid size={{ xs: 12 }}>
        <Grid container sx={NppesOuterBodyGrid}>
          <Grid size={{ xs: 4 }} sx={flexAlignEnd}>
            <Grid container sx={SearchNpiContainer}>
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={fontWeight.Weight[5]}
                  color={colors.fonts[4]}
                  sx={NppesNPISearchPadding}
                  display="inline"
                >
                  Search by NPI
                </Typography>
                <Typography
                  variant="subtitle2"
                  fontWeight={fontWeight.Weight[2]}
                  color={colors.black[2]}
                  sx={NppesNPIPadding}
                  display="inline"
                >
                  (Requires full NPI number to view the results):
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Grid container>
                  <Controller
                    name={"npi"}
                    control={control}
                    defaultValue={null}
                    render={({ fieldState: { error } }) => {
                      return (
                        <>
                          <VirtualizeAutocomplete
                            options={PhysicianListByNPIResp}
                            value={physicianInitialData}
                            handleInputChange={handleInputChange}
                            sx={NppesAutocompleteUI}
                            open={NppesOpen}
                            handleSelect={handleSelect}
                            tableHead={getTableHead(TableDropdown.PHYSICIAN)}
                            tableBody={getTableBody(TableDropdown.PHYSICIAN)}
                            autocompleteInputIcon={<IconFaUser />}
                            setOpen={NppesSetOpen}
                            maxLength={10}
                            width="55rem"
                            loading={loading}
                            placeholder="Enter NPI number"
                            InputType="onlyNumber"
                            handleFocus={handleFocus}
                            hideCrossIcon={true}
                            setClearBlur={false}
                            injectedTo={`${nppesSearchInjectedto}-search-by-npi`}
                          />
                          {error && (
                            <FormHelperText>{error.message}</FormHelperText>
                          )}
                        </>
                      );
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 0.5 }} sx={flexAlignCentre}>
            <Grid container sx={flexAllCentre}>
              <Typography
                variant="subtitle2"
                fontWeight={fontWeight.Weight[5]}
                color={colors.fonts[4]}
              >
                OR
              </Typography>
            </Grid>
          </Grid>
          <Grid size={{ xs: 7.5 }} sx={flexAlignEnd}>
            <Grid container sx={{ width: "100%" }}>
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={fontWeight.Weight[5]}
                  color={colors.fonts[4]}
                >
                  Search by Name/State
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Box height={"2.859rem"}>
                  <Grid container gap={"0.5rem"}>
                    <Grid size={{ xs: 2.5 }}>
                      <Box sx={NppesAutoCompleteNameInputMargin}>
                        <Controller
                          name={"lastName"}
                          control={control}
                          defaultValue={null}
                          rules={{ validate: validateNPPESNames }}
                          render={({ fieldState: { error } }) => {
                            return (
                              <>
                                <Autocomplete
                                  options={PhysicianListByNameResp}
                                  data-testid={`${nppesSearchInjectedto}-nppes-last-Name`}
                                  value={null}
                                  clearIcon={null}
                                  freeSolo
                                  getOptionLabel={(option: any) =>
                                    option.physicianName
                                  }
                                  slots={{
                                    paper: customPaper,
                                    popper: StyledPopper,
                                  }}
                                  loading={loading}
                                  filterOptions={(options) => options}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant="standard"
                                      placeholder="Enter Last Name"
                                      slotProps={{
                                        input: {
                                          ...params.InputProps,
                                        },
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          e.stopPropagation();
                                        }
                                      }}
                                      inputRef={lastNameFieldRef}
                                    />
                                  )}
                                  renderOption={(_props, option) => {
                                    return (
                                      <Box
                                        sx={rowMaxWidth}
                                        key={option.physicianNpiId}
                                      >
                                        {option.physicianNpiId === "" && (
                                          <Box sx={AvailableRecords}>
                                            <Typography
                                              variant="subtitle2"
                                              color={colors.black[2]}
                                              fontWeight={fontWeight.Weight[4]}
                                            >
                                              Available records :
                                            </Typography>
                                          </Box>
                                        )}
                                        {option.physicianNpiId === "" ? (
                                          <Box sx={NppesContainer.header.row}>
                                            {header(
                                              "left",
                                              "Name:",
                                              NppesContainer.header.cell1
                                            )}
                                            {header(
                                              "left",
                                              "NPI Number:",
                                              NppesContainer.header.cell2
                                            )}
                                            {header(
                                              "left",
                                              "Address:",
                                              NppesContainer.header.cell3
                                            )}
                                            {header(
                                              "left",
                                              "Action:",
                                              NppesContainer.header.cell4
                                            )}
                                          </Box>
                                        ) : (
                                          <Box sx={NppesContainer.body.row}>
                                            {body(
                                              option.physicianName,
                                              false,
                                              NppesContainer.body.cell1
                                            )}
                                            {body(
                                              option.physicianNpiId,
                                              false,
                                              NppesContainer.body.cell2
                                            )}
                                            {body(
                                              option.physicianAddress,
                                              false,
                                              NppesContainer.body.cell3
                                            )}
                                            {body(
                                              "",
                                              true,
                                              NppesContainer.body.cell4,
                                              option
                                            )}
                                          </Box>
                                        )}
                                      </Box>
                                    );
                                  }}
                                  sx={DatePickerInput}
                                  inputValue={lastNameinputValue}
                                  onInputChange={(_event, newInputValue) =>
                                    setLastNameInputValue(newInputValue)
                                  }
                                  onOpen={() => {
                                    if (
                                      Array.isArray(PhysicianListByNameResp) &&
                                      length(PhysicianListByNameResp)
                                    ) {
                                      setOpenAutocomplete(true);
                                    }
                                  }}
                                  open={openAutocomplete}
                                  fullWidth
                                />
                                {error && (
                                  <FormHelperText sx={NppesErrorMsgStyle}>
                                    {error.message}
                                  </FormHelperText>
                                )}
                              </>
                            );
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 2.5 }}>
                      <Box sx={NppesAutoCompleteNameInputMargin}>
                        <Controller
                          name={"firstName"}
                          rules={{ validate: validateNPPESNames }}
                          control={control}
                          defaultValue={null}
                          render={() => {
                            return (
                              <>
                                <Autocomplete
                                  options={[]}
                                  value={null}
                                  freeSolo
                                  getOptionLabel={(option: any) =>
                                    option.physicianName
                                  }
                                  data-testid={`${nppesSearchInjectedto}-nppes-first-Name`}
                                  slots={{
                                    paper: customPaper,
                                    popper: StyledPopper,
                                  }}
                                  clearIcon={null}
                                  renderInput={(
                                    params: AutocompleteRenderInputParams
                                  ) => (
                                    <TextField
                                      {...params}
                                      variant="standard"
                                      placeholder="Enter First Name"
                                      slotProps={{
                                        input: {
                                          ...params.InputProps,
                                        },
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          e.stopPropagation();
                                        }
                                      }}
                                      inputRef={firstNameFieldRef}
                                    />
                                  )}
                                  sx={DatePickerInput}
                                  inputValue={firstNameInputValue}
                                  onInputChange={(_event, newInputValue) =>
                                    setFirstNameInputValue(newInputValue)
                                  }
                                  onOpen={() => {
                                    if (
                                      Array.isArray(PhysicianListByNameResp) &&
                                      length(PhysicianListByNameResp)
                                    ) {
                                      setOpenAutocomplete(true);
                                    }
                                  }}
                                  fullWidth
                                />
                              </>
                            );
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 4 }} sx={NppesStateSearchWidth}>
                      <Box sx={NppesAutoCompleteNameInputMargin}>
                        <Controller
                          name={"state"}
                          control={control}
                          defaultValue={null}
                          render={() => {
                            return (
                              <>
                                <Autocomplete
                                  sx={NppesAutoComplete}
                                  data-testid={`${nppesSearchInjectedto}-nppes-state`}
                                  {...defaultProps(stateList)}
                                  id="auto-complete"
                                  autoComplete
                                  onChange={(e, v) => {
                                    onSelection(e, v);
                                  }}
                                  clearIcon={null}
                                  includeInputInList
                                  value={stateName || null}
                                  noOptionsText={NO_RECORD_FOUND}
                                  slots={{
                                    paper: customPaper,
                                    popper: StyledPopperCheckBoxAutocomplete,
                                  }}
                                  disabled={isNil(stateList) ? true : false}
                                  isOptionEqualToValue={(option, value) =>
                                    option.stateId === value.stateId &&
                                    option.stateName === value.stateName
                                  }
                                  filterOptions={(options, { inputValue }) => {
                                    const lowercasedInput =
                                      inputValue.toLowerCase();
                                    return options.filter(
                                      (option) =>
                                        option.stateName
                                          .toLowerCase()
                                          .includes(lowercasedInput) ||
                                        option.stateId
                                          .toLowerCase()
                                          .includes(lowercasedInput)
                                    );
                                  }}
                                  onOpen={onOpen}
                                  onClose={onClose}
                                  clearOnBlur={true}
                                  renderInput={(params) => {
                                    params.inputProps.maxLength = 50;
                                    return (
                                      <Box sx={Search}>
                                        <TextField
                                          {...params}
                                          variant="standard"
                                          placeholder="Choose State(Optional)"
                                          slotProps={{
                                            input: {
                                              ...params.InputProps,
                                              startAdornment: (
                                                <>
                                                  <InputAdornment position="start">
                                                    <IconFaSearch />
                                                  </InputAdornment>
                                                  {
                                                    params.InputProps
                                                      .startAdornment
                                                  }
                                                </>
                                              ),
                                            },
                                          }}
                                          sx={DatePickerInput}
                                        />
                                      </Box>
                                    );
                                  }}
                                  onInputChange={(_event, value) =>
                                    value !== "" && onOpen
                                  }
                                  open={open}
                                  renderOption={(props, option) => {
                                    return (
                                      <li {...props} key={option.stateId}>
                                        {option.stateName}
                                      </li>
                                    );
                                  }}
                                />
                              </>
                            );
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <Box sx={NppesAutoCompleteNameInputMargin}>
                        <Button
                          onClick={handleSearch}
                          variant="contained"
                          data-testid={`${nppesSearchInjectedto}-nppes-search`}
                        >
                          SEARCH
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
            <Grid container></Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default NppesSearchComponent;
